/**
 * ProposalHero — Hero savings metric + cost comparison bars
 *
 * Shows the headline savings number, savings percentage,
 * and a horizontal bar comparison of BAU vs new cost.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { vortxColors, vortxTypography, chartColors } from '@vortx/shared-ui';
import { formatCurrency, formatPercent } from './extractProposalData';

export default function ProposalHero({ data }) {
  const {
    bauAnnualCost,
    year1NetSavings,
    year1TotalNewCost,
    year1AnnualSavings,
    year1EsaPayment,
    year1OM,
    optimizedUtilityBill,
    savingsPercent,
    mode,
  } = data;

  // All-in cost: utility + ESA payment (ESA) or utility + O&M (CapEx)
  const newCost = mode === 'esa'
    ? year1TotalNewCost ?? optimizedUtilityBill
    : optimizedUtilityBill + (year1OM || 0);

  // Headline savings
  const headlineSavings = mode === 'esa' ? year1NetSavings : year1AnnualSavings;

  // Bar widths — BAU is 100%, new cost proportional
  const newCostPct = bauAnnualCost > 0 ? Math.max((newCost / bauAnnualCost) * 100, 2) : 50;

  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      {/* Headline savings */}
      <Typography
        sx={{
          fontFamily: vortxTypography.heading,
          fontWeight: 800,
          fontSize: { xs: '2rem', sm: '2.75rem' },
          color: chartColors.savings,
          lineHeight: 1.1,
        }}
      >
        Save {formatCurrency(headlineSavings)}/yr
      </Typography>
      <Typography
        sx={{
          fontFamily: vortxTypography.mono,
          fontSize: '1.1rem',
          color: vortxColors.stone,
          mt: 0.5,
        }}
      >
        {formatPercent(savingsPercent, 0)} reduction in energy costs
      </Typography>

      {/* Cost comparison bars */}
      <Box sx={{ mt: 4, maxWidth: 480, mx: 'auto' }}>
        {/* BAU bar */}
        <CostBar
          label="BAU Utility Cost"
          amount={bauAnnualCost}
          widthPct={100}
          color={vortxColors.stone}
          bgAlpha={0.25}
        />

        {/* All-in cost bar */}
        <Box sx={{ mt: 2 }}>
          <CostBar
            label="All-in Cost"
            amount={newCost}
            widthPct={newCostPct}
            color={vortxColors.gold}
            bgAlpha={0.35}
          />
          {mode === 'esa' && year1EsaPayment != null && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                mt: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                Utility: {formatCurrency(optimizedUtilityBill)}
              </Typography>
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                ESA: {formatCurrency(year1EsaPayment)}
              </Typography>
            </Box>
          )}
          {mode === 'capex' && year1OM != null && year1OM > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                mt: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                Utility: {formatCurrency(optimizedUtilityBill)}
              </Typography>
              <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                O&M: {formatCurrency(year1OM)}
              </Typography>
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.3)', mt: 1, display: 'block' }}
          >
            Utility cost + {mode === 'esa' ? 'ESA payments' : 'O&M'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function CostBar({ label, amount, widthPct, color, bgAlpha }) {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: vortxColors.white }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: vortxTypography.mono,
            fontWeight: 700,
            fontSize: '0.95rem',
            color,
          }}
        >
          {formatCurrency(amount)}
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          height: 12,
          borderRadius: 1,
          bgcolor: `rgba(255,255,255,0.06)`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${widthPct}%`,
            height: '100%',
            borderRadius: 1,
            bgcolor: color,
            opacity: bgAlpha + 0.3,
            transition: 'width 0.6s ease',
          }}
        />
      </Box>
    </Box>
  );
}
